import { featureCollection, point, polygon } from "@turf/helpers";
import { area, bbox, distance, intersect } from "@turf/turf";
import { union } from "@turf/union";
import React, { useEffect, useRef } from "react";
import { MapFacadeMaplibre, MitLayerHandleMaplibre } from "src/managers/MapFacadeMaplibre";
import { actionSetSingleFeatureLayerVisibility, MapitStateContext, SetPrintLayers } from "src/states/MapitState";
import { GlassButton } from "src/components/GlassButtons";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { openWindow, sendParams, WindowId } from "src/WindowManager/WindowState";
import { BNBOContext, BNBOLodsejer, BNBOOmrådeType, BNBOState } from "./BNBOState";
import { VectorLayer } from "src/managers/VectorLayerFunc";
import { GeojsonWFS } from "src/managers/WFSGeojsonLayerFunc";
import { FeatureLayer } from "src/managers/WmsLayerFunc";
import { PromiseImage } from "src/functions/PaperCanvas";
import { formatMatrikelNumre } from "./BNBOFunc";
import { SettingsManager } from "@viamap/viamap2-common";
import { GenerateGeom } from "src/managers/GenerateGeom";
import { colorByType, textByType } from "./BNBOAreaEditorExtra";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "src/components/GlassInput";
import polylabel from "polylabel"
import { GlassCheckbox } from "src/components/MitGlassComponents";

// eslint-disable-next-line react-refresh/only-export-components
export enum ViewerModus {
   Overblik = 0,
   ERids = 1,
   ERidsUU = 2,
   Kortbilag = 3,
   Ingen = 4,
   Ingen2 = 5
}

export function BNBOAreaViewer(props: {
   lodsejerId: string,
   features: any[]
   bnboGeom: any[]
   markGeom: any[]
   lodsGeom: any[],
   defaultModus?: ViewerModus
}) {
   const { state: mapitState, dispatch: mapitStateDispatch } = React.useContext(MapitStateContext);
   const map = useRef<MapFacadeMaplibre | null>(null);
   const { dispatch: WindowDispatch } = useWindowContext();
   const { state: bnboState } = React.useContext(BNBOContext);
   const [modus, setModus] = React.useState<ViewerModus>(ViewerModus.Overblik);
   const [mitLayers, setMitLayers] = React.useState<any[]>([]);
   const [featureLayers, setFeatureLayers] = React.useState<any[]>([]);
   const [tolerance, setTolerance] = React.useState<number>(SettingsManager.getSystemSetting("bnbo.geojsonPrintTolerance", 0.2) || 0.1);
   const [areaDef, setAreaDef] = React.useState<"M2" | "hektar" | "none">("none");
   const [specialAreasEnabled, setSpecialAreasEnabled] = React.useState<boolean>(SettingsManager.getSystemSetting("bnbo.areaViewerSpecialAreas", true) || true);
   const [arealCombinering, setArealCombinering] = React.useState<number>(100);

   const legend = getLegendURLbyModus(modus);

   const [legendImg, setLegendImg] = React.useState<string[]>([]);
   useEffect(() => {
      getLegendPerLayer(props.features, modus, specialAreasEnabled).then((a) => setLegendImg(a))
   }, [modus, props.features, specialAreasEnabled])

   useEffect(() => {
      setModus(props.defaultModus || ViewerModus.Overblik)
   }, [props.defaultModus]);

   function turnAllLayersOff() {
      mapitState.selectedFeatureLayerKeys.forEach(key => {
         mapitStateDispatch(actionSetSingleFeatureLayerVisibility(key, false))
      });
   }

   function turnLayersOn(key: string): VectorLayer | GeojsonWFS | FeatureLayer | undefined {
      let layerSpec = mapitState.featureLayers[key];
      if (layerSpec instanceof VectorLayer || layerSpec instanceof GeojsonWFS) {
         map.current?.addVectorLayer(layerSpec)
         return layerSpec
      } else if (layerSpec instanceof FeatureLayer) {
         map.current?.addFeatureLayer(layerSpec.layer.label, layerSpec)
         return layerSpec
      }
   }

   useEffect(() => {
      if (!map.current) {
         return
      }


      let BNBOLayerSourceViewbnboGeom = emptySource(props.bnboGeom, tolerance) as any;
      let BNBOLayerViewbnboGeom = bnboLayerStylingbnboGeom("BNBOLayerViewbnboGeomfill", "BNBOLayerSourceViewbnboGeom") as any;
      // let BNBOLayerViewbnboGeomFill = bnboLayerStylingbnboGeomFill("BNBOLayerViewbnboGeomFill", "BNBOLayerSourceViewbnboGeom") as any;
      if (!(modus === ViewerModus.ERids || modus === ViewerModus.ERidsUU)) {
         let BNBOLayerSourceViewmarkGeom = emptySource(props.markGeom, tolerance) as any;
         let BNBOLayerViewmarkGeom = bnboLayerStylingmarkGeom("BNBOLayerViewmarkGeomline", "BNBOLayerSourceViewmarkGeom") as any;
         let BNBOLayerViewmarkGeomFill = bnboLayerStylingmarkGeomFill("BNBOLayerViewmarkGeomfill", "BNBOLayerSourceViewmarkGeom") as any;

         let ViewMark = new MitLayerHandleMaplibre()
         ViewMark.addSource("BNBOLayerSourceViewmarkGeom", BNBOLayerSourceViewmarkGeom)
         ViewMark.addLayer(BNBOLayerViewmarkGeom)
         ViewMark.addLayer(BNBOLayerViewmarkGeomFill)
      }
      let BNBOLayerSourceViewlodsGeom = emptySource(props.lodsGeom, tolerance) as any;
      let BNBOLayerViewlodsGeom = bnboLayerStylinglodsGeom("BNBOLayerViewlodsGeomline", "BNBOLayerSourceViewlodsGeom") as any;
      let BNBOLayerViewlodsGeomFill = bnboLayerStylinglodsGeomFill("BNBOLayerViewlodsGeomfill", "BNBOLayerSourceViewlodsGeom") as any;


      let BNBOLayerSourceView = emptySource(FeaturesByModus(props.features, modus), tolerance) as any;

      let BNBOLayerView = (modus == ViewerModus.ERids || modus == ViewerModus.ERidsUU) ? bnboLayerStylingeRids("BNBOLayerViewfill", "BNBOLayerSourceView") as any : bnboLayerStyling("BNBOLayerViewfill", "BNBOLayerSourceView") as any
      let BNBOSpecial = specialAreasEnabled ? bnboSpecialLayerStyling("BNBOLayerViewSpecial", "BNBOLayerSourceView") : undefined;

      let areaView = new MitLayerHandleMaplibre()

      let eRidsAreaCalg = (modus == ViewerModus.ERids || modus == ViewerModus.ERidsUU) && getDeclarationAreal({ data: featureCollection(BNBOLayerSourceView.data.features) } as any, areaDef, arealCombinering)
      let eRidsAreaLayer = emptySource(eRidsAreaCalg ? eRidsAreaCalg : [], tolerance);
      let eRidsAreaLayerStyling = getAreaTextLayerStyling("eRidsAreaLayer", "eRidsAreaLayerSrc") as any;
      areaView.addSource("eRidsAreaLayerSrc", eRidsAreaLayer)
      areaView.addLayer(eRidsAreaLayerStyling)
      // let BNBOLayerViewAreaCalc = bnboLayerStyling("BNBOLayerViewAreaCalc", "BNBOLayerSourceView") as any
      let View = new MitLayerHandleMaplibre()
      View.addSource("BNBOLayerSourceView", BNBOLayerSourceView)
      View.addLayer(BNBOLayerView)
      // View.addLayer(BNBOLayerViewAreaCalc)
      BNBOSpecial && View.addLayer(BNBOSpecial)
      // View.addLayer(BNBOLayerViewFill)
      let ViewBnbo = new MitLayerHandleMaplibre()
      ViewBnbo.addSource("BNBOLayerSourceViewbnboGeom", BNBOLayerSourceViewbnboGeom)
      ViewBnbo.addLayer(BNBOLayerViewbnboGeom)
      // ViewBnbo.addLayer(BNBOLayerViewbnboGeomFill)
      let ViewLods = new MitLayerHandleMaplibre()
      ViewLods.addSource("BNBOLayerSourceViewlodsGeom", BNBOLayerSourceViewlodsGeom)
      ViewLods.addLayer(BNBOLayerViewlodsGeom)
      ViewLods.addLayer(BNBOLayerViewlodsGeomFill)
      let printObject: typeof mapitState["printLayers"] = [
         { type: "MitLayer", handle: View },
         { type: "MitLayer", handle: ViewBnbo },
         { type: "MitLayer", handle: ViewLods },
         { type: "MitLayer", handle: areaView },
      ];
      printObject.forEach((a) => {
         let layerId = (a as any).handle.getLayerIds();
         let placeBefore = layerId && getPlaceBefore(map.current!, layerId);
         if (placeBefore) {
            let duckType = [{ getLayerIds: () => [placeBefore] }]
            map.current && (((a as any).handle as MitLayerHandleMaplibre).addTo(map.current, duckType as any))
         } else {
            map.current && (((a as any).handle as MitLayerHandleMaplibre).addTo(map.current))
         }
      });
      setMitLayers(printObject)

      return () => {
         setMitLayers([])
         printObject.forEach((a) => {
            (a.handle as MitLayerHandleMaplibre).clearLayers(map.current as any)
         });
      }
   }, [map.current, modus, areaDef, tolerance, props.features, props.bnboGeom, props.markGeom, props.lodsGeom, arealCombinering, specialAreasEnabled]);

   useEffect(() => {
      let printObject: typeof mapitState["printLayers"] = [];
      map.current = mapitState.map
      let [ba, bb, bc, bd] = bbox(featureCollection([...props.features, ...props.lodsGeom]));
      map.current.getMapPop().fitBounds([[ba - 0.0005, bb - 0.0005], [bc + 0.0005, bd + 0.0005]])

      printObject.forEach((a) => {
         map.current && (((a as any).handle as MitLayerHandleMaplibre).addTo(map.current))
      });
      let layers = getLayersByModus(modus).map((a) => turnLayersOn(a)).filter((a) => a);
      layers.forEach((a) => {
         if (!a) { return }
         if (a instanceof VectorLayer || a instanceof GeojsonWFS) {
            printObject = [...printObject, { type: "Vector", handle: a as VectorLayer }]
         } else if (a instanceof FeatureLayer) {
            printObject = [{ type: "Raster", handle: a as FeatureLayer }, ...printObject,]
         }
      });
      // let lastLayers: typeof mapitState["printLayers"] = [{type: "MitLayer", handle:ViewLods}, {type: "MitLayer", handle:ViewBnbo}, {type: "MitLayer", handle:View}]
      // lastLayers.forEach((a) => {
      //    map.current && (((a as any).handle as MitLayerHandleMaplibre).addTo(map.current))
      // })
      let topLayers = getTopLayerByModus(modus).map((a) => turnLayersOn(a)).filter((a) => a);
      let topLayerObj = topLayers.map((a) => {
         if (!a) { return }
         if (a instanceof VectorLayer || a instanceof GeojsonWFS) {
            return { type: "Vector", handle: a as VectorLayer }
         } else if (a instanceof FeatureLayer) {
            console.warn("Top layer is a raster layer, this should not be the case")
         }
      }) as any
      printObject = [...printObject, ...topLayerObj]
      setFeatureLayers(printObject)
      // mapitStateDispatch(SetPrintLayers(printObject))
      return () => {
         setFeatureLayers([])
         WindowDispatch(sendParams(WindowId.PlotScreen, {}))
         // mapitStateDispatch(SetPrintLayers([]))
         console.log(printObject)
         Object.values(printObject).forEach((a) => {
            if (a?.type === "Raster" && a.handle instanceof FeatureLayer) {
               console.log("Removing raster layer")
               map.current?.removeFeatureLayer(a.handle.layer.label)
            } else if (a?.type === "Vector" && (a.handle instanceof GeojsonWFS || a.handle instanceof VectorLayer)) {
               console.log("Removing vector layer")
               map.current?.removeVectorLayer(a.handle)
            } else if (a?.type === "MitLayer" && a.handle instanceof MitLayerHandleMaplibre) {
               a.handle.clearLayers(map.current as any)
            }
         });
      }
   }, [modus])


   useEffect(() => {
      let [ba, bb, bc, bd] = bbox(featureCollection([...props.features, ...props.lodsGeom]));
      map.current?.getMapPop().fitBounds?.([[ba - 0.0005, bb - 0.0005], [bc + 0.0005, bd + 0.0005]]);
      setModus(ViewerModus.Overblik)
   }, [props.lodsejerId])

   return (
      <>
         <div style={{ marginTop: "5px" }}></div>
         <GlassInputControl>
            <select
               value={modus}


               onChange={(e) => {
                  turnAllLayersOff()
                  setModus(parseInt(e.target.value) as ViewerModus)
               }} >
               <option value={ViewerModus.Overblik}>{ModusToString(ViewerModus.Overblik)}</option>
               <option value={ViewerModus.ERids}>{ModusToString(ViewerModus.ERids)}</option>
               <option value={ViewerModus.ERidsUU}>{ModusToString(ViewerModus.ERidsUU)}</option>
               <option value={ViewerModus.Kortbilag}>{ModusToString(ViewerModus.Kortbilag)}</option>
               {/* <option value={ViewerModus.Ingen}>{ModusToString(ViewerModus.Ingen)}</option> */}
               {/* <option value={ViewerModus.Ingen2}>{ModusToString(ViewerModus.Ingen2)}</option> */}
            </select>
         </GlassInputControl>
         <div style={{ marginTop: "5px" }}></div>
         <GlassInputGroup>
            <GlassTextInputTransformer label={"Print tolerance"} >
               <input
                  type="range" min={0.000001} max={0.2} step={0.000001} defaultValue={tolerance} onChange={(e) => {
                     setTolerance(parseFloat(e.target.value))
                  }} />
            </GlassTextInputTransformer>
            <GlassTextInputTransformer label={"Speacial områder"}>
               <GlassCheckbox checked={specialAreasEnabled} onClick={(e, checked) => setSpecialAreasEnabled(checked)} />
            </GlassTextInputTransformer>
            {
               modus === ViewerModus.ERids || modus === ViewerModus.ERidsUU ?
                  <>

                     <GlassTextInputTransformer label={"Areal angivelse"}>
                        <GlassInputControl>
                           <select defaultValue={areaDef} onChange={(e) => { setAreaDef(e.target.value as "M2" | "hektar") }}>
                              <option value={"none"}>Ingen</option>
                              <option value={"M2"}>M2</option>
                              <option value={"hektar"}>hektar</option>
                           </select>
                        </GlassInputControl>
                     </GlassTextInputTransformer>
                     <GlassTextInputTransformer label={"Areal summering"}>
                        {/* <GlassInputControl> */}
                        <input
                           type="range" min={0} step={1} max={5000} defaultValue={arealCombinering} onChange={(e) => {
                              setArealCombinering(parseInt(e.target.value))
                           }} />
                        {/* </GlassInputControl> */}
                     </GlassTextInputTransformer>
                  </>
                  : null
            }
         </GlassInputGroup>

         <br />


         <h6>Signaturforklaring</h6>
         {legend.map((a, idx) => (<img key={idx} src={a} alt="Signaturforklaring" />))}
         {legendImg.map((a, idx) => (<img key={idx} src={a} alt="Signaturforklaring" />))}
         <div style={{ display: "flex", marginTop: "auto", justifyContent: "right", gap: "5px" }} >
            {/* <GlassButton 
         onClick={(e) => window.open(new URL("/demoDataBNBO/BNBO Kortbilag.pdf", import.meta.url).href, "_blank")}
      >Kortbilag</GlassButton>
      <BNBOGenerateERids lodsEjer={props.lodsejerId} /> */}
            <GlassButton
               onClick={() => {
                  let printLayers = [...mitLayers, ...featureLayers].toSorted((a, b) => {
                     let aLayer = a.handle?.getLayerIds?.()[0] ?? a.handle.layer.label;
                     let bLayer = b.handle?.getLayerIds?.()[0] ?? b.handle.layer.label;
                     let aIndex = bnboOrder().indexOf(aLayer) ?? 99;
                     let bIndex = bnboOrder().indexOf(bLayer) ?? 99;
                     return aIndex - bIndex;
                  });
                  mapitStateDispatch(SetPrintLayers(printLayers as any))
                  let description = getDescriptionByType(modus, bnboState.lodsejer[props.lodsejerId], bnboState, props.features);
                  WindowDispatch(openWindow(WindowId.PlotScreen, {
                     externalSettings: {
                        format: "A3L",
                        title: "",
                        includeDate: false,
                        description: description,
                        legendImg: [...legend.map((a) => ({ img: PromiseImage(a), ftl: { layer: {} } })), ...legendImg.map((a) => ({ img: PromiseImage(a), ftl: { layer: {} } }))],
                     }
                  }))
               }} >
               Plot {ModusToString(modus)}</GlassButton>
         </div>
      </>
   )
}


function bnboLayerStylingeRids(id: string, source: string) {
   return {
      id: id,
      type: 'fill',
      source: source,
      paint: {
         'fill-color': ["match", ["get", 'type'],
            'ServitutAreal', 'lightblue',
            'UlempeAreal', 'lightblue',
            "AndetAreal", 'lightblue',
            "FredskovsAreal", 'lightblue',
            "Par3Areal", 'lightblue',
            "FradragsAreal", 'lightblue',
            "Vejareal", 'lightblue',
            "Restareal", 'lightblue',
            "MarkServitutareal", 'lightblue',
            '#0000'],
         'fill-opacity': ["case",
            ["!", ["boolean", ["get", "visible"], true]], 0.1,
            ['boolean', ['feature-state', 'hover'], false], 1, 0.6],
         // 'fill-outline-color': ["case", ['boolean', ['feature-state', 'hover'], false],"white",['coalesce',["get",'color'],'lightblue']]
      }
   }
}

function bnboSpecialLayerStyling(id: string, source: string) {
   return {
      id: id,
      type: 'fill',
      source: source,
      paint: {
         'fill-pattern': ['coalesce', ["get", "specialPattern"], "mit-patt-line-#000"],
         'fill-outline-color': "#000a",
      },
      filter: ["boolean", ["get", "isSpecialArea"], false]
   }
}


function bnboLayerStyling(id: string, source: string) {
   return {
      id: id,
      type: 'fill',
      source: source,
      paint: {
         'fill-color': ["to-color", ["get", "farve"], ["match", ["get", 'type'],
            'ServitutAreal', 'blue',
            'UlempeAreal', 'lightblue',
            "AndetAreal", 'red',
            "FredskovsAreal", 'green',
            "Par3Areal", 'purple',
            "FradragsAreal", 'yellow',
            "Restareal", 'orange',
            "Vejareal", 'brown',
            "MarkServitutareal", 'blue',
            'grey']],
         'fill-opacity': ["case",
            ["!", ["boolean", ["get", "visible"], true]], 0.1,
            ['boolean', ['feature-state', 'hover'], false], 1, 0.6],
         // 'fill-outline-color': ["case", ['boolean', ['feature-state', 'hover'], false],"white",['coalesce',["get",'color'],'lightblue']]
      }
   }
}


function bnboLayerStylingbnboGeom(id: string, source: string) {
   return {
      id: id,
      type: 'line',
      source: source,
      paint: {
         'line-color': "red",
         'line-width': 3,
      }
   }
}
function bnboLayerStylingmarkGeomFill(id: string, source: string) {
   return {
      id: id,
      type: 'fill',
      source: source,
      paint: {
         'fill-pattern': "mit-patt-line-#add86f-#ddd",
      }
   }
}
function bnboLayerStylingmarkGeom(id: string, source: string) {
   return {
      id: id,
      type: 'line',
      source: source,
      paint: {
         'line-color': "#add86f",
         'line-width': 3,
      }
   }
}
function bnboLayerStylinglodsGeomFill(id: string, source: string) {
   return {
      id: id,
      type: 'fill',
      source: source,
      paint: {
         'fill-color': "yellow",
         'fill-opacity': 0
      }
   }
}
function bnboLayerStylinglodsGeom(id: string, source: string) {
   return {
      id: id,
      type: 'line',
      source: source,
      paint: {
         'line-color': "yellow",
         'line-width': 3,
      }
   }
}
function getAreaTextLayerStyling(id, source) {
   return {
      type: "symbol", id: id, source: source, layout: {
         "text-field": ["get", "decAreal"],
         "text-size": 18,
         "text-font": ["Noto Sans Regular"],
         "icon-ignore-placement": true,
         "text-allow-overlap": true,
         "icon-allow-overlap": true
      }, paint: { "text-color": "#f00", "text-halo-color": "#000", "text-halo-width": 1, "text-halo-blur": 1 }
   } as any;
}

function emptySource(a: any[], tolerance?: number) {
   return {
      'type': 'geojson',
      'tolerance': tolerance ?? (SettingsManager.getSystemSetting("bnbo.geojsonPrintTolerance", 0.2) || 0.1),
      'data': (a && featureCollection(a)) || featureCollection([]),
   }
}




function ModusToString(modus: ViewerModus) {
   return {
      [ViewerModus.Overblik]: "Overblik",
      [ViewerModus.ERids]: "eRids - Med Ulempeareal",
      [ViewerModus.ERidsUU]: "eRids - Uden Ulempeareal",
      [ViewerModus.Kortbilag]: "Kortbilag",
      [ViewerModus.Ingen]: "Ingen",
      [ViewerModus.Ingen2]: "Ingen2"
   }[modus] || "Ukendt modus"
}


function getDescriptionByType(modus: ViewerModus, lodsEjer: BNBOLodsejer, bnboState: BNBOState, features: any[]) {
   const dele = FeaturesByModus(features, modus) as any[]
   const matrikel_aftale = lodsEjer.geometry.features.filter((a) => dele.some((b) => intersect(featureCollection([a, b]))))
   const Matrikel_Liste_aftale = matrikel_aftale && formatMatrikelNumre(matrikel_aftale) || "[[Matrikel liste BNBO]]"


   switch (modus) {
      case ViewerModus.Overblik: return `Overblik over ${lodsEjer.sfeNummer}`
      case ViewerModus.ERidsUU:
      case ViewerModus.ERids: return `eRids udfærdiget i forbindelse med tinglysning af servitut
      
      SFE: ${lodsEjer.sfeNummer}
      Sagsbehandler: ${lodsEjer.sagsbehandler} \n
      Matr.nre. \n ${Matrikel_Liste_aftale}
      
      Kommune: ${SettingsManager.getSystemSetting("bnbo.kommuneNavn") || ""} 
      Dato: ${new Date().toLocaleDateString("da-dk")}`
      case ViewerModus.Kortbilag: return `Kortbilag for ${lodsEjer.sfeNummer}`
      default: return "Missing"

   }
}

async function getLegendPerLayer(inFeatures, modus: ViewerModus, specialAreasEnabled): Promise<string[]> {

   let featuresSpecialArea = specialAreasEnabled ? FeaturesByModus(inFeatures, modus).filter((a) => a.properties.isSpecialArea) : []
   let reducedFeatSpecialArea = featuresSpecialArea.reduce((acc, feature) => {
      if (acc.length === 0) {
         return [feature]
      }
      if (acc.find((a) => a.properties.specialNote === feature.properties.specialNote)) {
         return acc
      }
      return [...acc, feature]
   }, [])

   let SpecialSignature = await Promise.all(reducedFeatSpecialArea.map(async (a) => {
      console.log(a)
      return await GenerateGeom.SVGToImage(LegendMitPattern(
         (a.properties.specialPattern || "mit-patt-line-#000000"),
         (a.properties.specialNote || "Ingen signatur")
      ), 290, 29)
   }))
   if (modus === ViewerModus.ERids || modus === ViewerModus.ERidsUU) {
      return SpecialSignature
   }
   if (modus !== ViewerModus.Kortbilag && modus !== ViewerModus.Overblik) {
      return []
   }
   let features = FeaturesByModus(inFeatures, modus)
   let reducedFeat = features.reduce((acc, feature) => {
      if (acc.length === 0) {
         return [feature]
      }
      if (acc.find((a) => a.properties.type === feature.properties.type
         && a.properties.note === feature.properties.note
         && a.properties.farve === feature.properties.farve
      )) {
         return acc
      }
      return [...acc, feature]
   }, [])

   return [...await Promise.all(reducedFeat.map(async (a) => {
      console.log(a)
      return await GenerateGeom.SVGToImage(LegendSVG(
         (a.properties.farve || colorByType(a.properties.type) || "lightblue"),
         (a.properties.note || textByType(a.properties.type) || "Ukendt type")
      ), 290, 29)
   })), ...SpecialSignature]
}



function FeaturesByModus(inFeatures, modus: ViewerModus) {
   let features = inFeatures.filter((a) => !(a.properties.type === BNBOOmrådeType.Inaktive))
   switch (modus) {
      case ViewerModus.ERidsUU:
         return features.filter((a) => !(a.properties.type === BNBOOmrådeType.UlempeAreal))
      case ViewerModus.ERids:
      case ViewerModus.Overblik:
      case ViewerModus.Kortbilag:
      case ViewerModus.Ingen:
      case ViewerModus.Ingen2:
         return features
   }
}

function getDeclarationAreal(source: ReturnType<typeof emptySource>, areaDef: "M2" | "hektar" | "none", opsumeringsDistance: number = 0) {
   if (areaDef === "none") {
      return null
   }
   let x = union(source.data as any)
   if (x) {
      if (x.geometry.type === "Polygon") {
         let x_area = area(x as any)
         let [pos_x, pos_y] = polylabel(x.geometry.coordinates as any, 0.00001)
         let areaText = areaDef === "hektar" ? `${(x_area / 10000).toFixed(2)} ha` : `${x_area.toFixed(2)} m²`
         return [point([pos_x, pos_y], { decAreal: areaText })]
      } else if (x.geometry.type === "MultiPolygon") {
         let Point_arr = x.geometry.coordinates.map((a) => {
            let x_area = area(polygon(a as any) as any)
            let [pos_x, pos_y] = polylabel(a as any, 0.00001)
            return point([pos_x, pos_y], { decAreal: x_area })
         })
         return Point_arr.reduce((acc, a) => {
            if (!acc) {
               return [a]
            }
            let x = acc.find((b) => (1000 * distance(a, b)) < opsumeringsDistance)
            if (x) {
               if (a.properties.decAreal > x.properties.decAreal) {
                  x.geometry = a.geometry
               }
               x.properties.decAreal += a.properties.decAreal
               return acc
            }
            return [...acc, a];
         }, [] as any).map((a) => {
            a.properties.decAreal = areaDef === "hektar" ? `${(a.properties.decAreal / 10000).toFixed(2)} ha` : `${a.properties.decAreal.toFixed(2)} m²`
            return a
         });
      }
   } else {
      return null
   }
}

function getPlaceBefore(map: MapFacadeMaplibre, a: string | Array<string>): string | undefined {
   let lOrder = bnboOrder();
   if (Array.isArray(a)) {
      a = a[0];
   }
   let index = lOrder.indexOf(a);
   if (index === -1) {
      console.error("Layer not found in bnbo plot order");
      return undefined;
   }
   let spliceIndex = lOrder.toSpliced(0, index + 1);
   let visibleLayers = map.getMapPop().getStyle()
   for (let beforeLayer of spliceIndex) {
      if (visibleLayers.layers.find((layer) => layer.id === beforeLayer)) {
         return beforeLayer
      }
   }
   return undefined
}

function bnboOrder() {
   return [
      "Ortofoto forår 12,5cm",
      "BNBOLayerView",
      "BNBOLayerViewfill",
      "BNBOLayerViewSpecial",
      "BNBOLayerViewbnboGeom",
      "BNBOLayerViewmarkGeom",
      "Ejerlavgrænser (Vektor)",
      "Optaget vej (Vektor)",
      "Jordstykke (Vektor)",
      "BNBOLayerViewlodsGeom",
      "Vandboringer §21b, stk. 2-areal",
      "Vandboringer",

      "eRidsAreaLayer",
      "JordstykkeNR (Vektor)",
      "BNBOLayerViewlodsGeomline",
      "BNBOLayerViewlodsGeomfill",
   ]
}

function getLayersByModus(modus: ViewerModus): (string)[] {
   return {
      [ViewerModus.Overblik]: [],
      [ViewerModus.ERidsUU]: ["Ortofoto forår 12,5cm", "Jordstykke (Vektor)", "Optaget vej (Vektor)", "Ejerlavgrænser (Vektor)", "Vandboringer §21b, stk. 2-areal", "Vandboringer"],
      [ViewerModus.ERids]: ["Ortofoto forår 12,5cm", "Jordstykke (Vektor)", "Optaget vej (Vektor)", "Ejerlavgrænser (Vektor)", "Vandboringer §21b, stk. 2-areal", "Vandboringer"],
      [ViewerModus.Kortbilag]: ["BNBOLayerView", "BNBOLayerViewbnboGeom", "BNBOLayerViewmarkGeom", "BNBOLayerViewlodsGeom"],
      [ViewerModus.Ingen]: ["BNBOLayerView", "BNBOLayerViewbnboGeom", "BNBOLayerViewmarkGeom", "BNBOLayerViewlodsGeom"],
      [ViewerModus.Ingen2]: ["BNBOLayerView", "BNBOLayerViewbnboGeom", "BNBOLayerViewmarkGeom", "BNBOLayerViewlodsGeom"]
   }[modus] || [];
}

function getTopLayerByModus(modus: ViewerModus): (string)[] {
   return {
      [ViewerModus.ERids]: ["JordstykkeNR (Vektor)"],
      [ViewerModus.ERidsUU]: ["JordstykkeNR (Vektor)"],
   }[modus] || []

}

function getLegendURLbyModus(modus: ViewerModus): string[] {
   return {
      [ViewerModus.Overblik]: [new URL("/demoDataBNBO/kortBilagSignaturSimple.png", import.meta.url).href],
      [ViewerModus.ERids]: [new URL("/demoDataBNBO/eRidsSignatur.png", import.meta.url).href],
      [ViewerModus.ERidsUU]: [new URL("/demoDataBNBO/eRidsSignatur.png", import.meta.url).href],
      [ViewerModus.Kortbilag]: [new URL("/demoDataBNBO/kortBilagSignaturSimple.png", import.meta.url).href],
      [ViewerModus.Ingen]: [""],
      [ViewerModus.Ingen2]: [""]
   }[modus] || [""];
}

function LegendSVG(color, text) {
   let width
   let font = 4.23333;
   let ctx = document.createElement("canvas").getContext("2d");
   if (ctx) {
      ctx.font = "4.23333px sans-serif";
      width = ctx.measureText(text).width + 15.599524;
      while (width > 76.940362) {
         font -= 0.1;
         ctx.font = `${font}px sans-serif`;
         width = ctx.measureText(text).width + 15.599524;
      }
   }

   return `<svg
   width="290.79822"
   height="29.836334"
   viewBox="0 -1 76.940362 7.8941968"
   version="1.1"
   id="svg1"
   xml:space="preserve"
   xmlns:xlink="http://www.w3.org/1999/xlink"
   xmlns="http://www.w3.org/2000/svg"
   xmlns:svg="http://www.w3.org/2000/svg"><defs
     id="defs1" /><g
     id="layer1"
     transform="translate(-0.49937871,-23.431982)"><text
       xml:space="preserve"
       style="font-size:${font}px;text-align:start;writing-mode:lr-tb;direction:ltr;text-anchor:start;fill:#000000;stroke-width:3.96875"
       x="15.599524"
       font-family="sans-serif"
       y="28.730408"
       id="text1-4-6">${text}
         </text>
         <rect
       style="fill:${color};fill-opacity:1;stroke-width:3.96875"
       id="rect1-5-1"
       width="10.986874"
       height="5.6706443"
       x="0.88603806"
       y="24.36945" /></g></svg>`
}

function LegendMitPattern(pattern, text) {
   let color = pattern.split("-")[3]
   let width
   let font = 4.23333;
   let ctx = document.createElement("canvas").getContext("2d");
   if (ctx) {
      ctx.font = "4.23333px sans-serif";
      width = ctx.measureText(text).width + 15.599524;
      while (width > 76.940362) {
         font -= 0.1;
         ctx.font = `${font}px sans-serif`;
         width = ctx.measureText(text).width + 15.599524;
      }
   }

   return `<svg
   width="290.79822"
   height="29.836334"
   viewBox="0 -1 76.940362 7.8941968"
   version="1.1"
   id="svg1"
   xml:space="preserve"
   xmlns:xlink="http://www.w3.org/1999/xlink"
   xmlns="http://www.w3.org/2000/svg"
   xmlns:svg="http://www.w3.org/2000/svg"><defs
     id="defs1" /><g
     id="layer1"
     transform="translate(-0.49937871,-23.431982)"><text
       xml:space="preserve"
       style="font-size:${font}px;text-align:start;writing-mode:lr-tb;direction:ltr;text-anchor:start;fill:#000000;stroke-width:3.96875"
       x="15.599524"
       font-family="sans-serif"
       y="28.730408"
       id="text1-4-6">${text}
         </text></g>
      <path
     style="fill:${color};fill-opacity:1"
     d="M 0.35957122,5.610041 V 4.6144859 L 2.201777,2.7737075 4.0439827,0.93292904 H 5.6514696 7.2589564 L 4.423571,3.7692626 1.5881856,6.6055961 H 0.97387843 0.35957122 Z M 6.8789043,3.7692626 9.7142897,0.93292904 h 0.8259743 0.825973 V 1.6320517 2.3311743 L 9.2277934,4.4683852 7.0893494,6.6055961 H 5.5664341 4.0435189 Z m 3.5983327,1.9896667 c 0.46392,-0.4656667 0.85373,-0.8466667 0.866245,-0.8466667 0.01252,0 0.02276,0.381 0.02276,0.8466667 V 6.6055961 H 10.499993 9.6337478 Z M 0.35957122,1.1869291 c 0,-0.22909128 0.0264622,-0.25400006 0.26984038,-0.25400006 H 0.89925197 L 0.65590454,1.1869291 c -0.13384109,0.1397 -0.25526926,0.254 -0.26984038,0.254 -0.0145711,0 -0.0264929,-0.1143 -0.0264929,-0.254 z"
     id="path13" /></svg>`



}